import { useState } from 'react'
import { ExternalLink, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import {
  PJ_NF_STATUS_CLASS,
  PJ_NF_STATUS_LABELS,
} from '@/components/PjNf/pjNfStatus'
import type { PjNfMensalRow } from '@/services/pjNfMensalService'
import { cn } from '@/utils/cn'
import { resolvePublicAssetUrl } from '@/utils/resolvePublicAssetUrl'

type Props = {
  open: boolean
  loading: boolean
  nf: PjNfMensalRow | null
  onClose: () => void
  onMarcarEmValidacao: (id: string) => Promise<boolean>
  onValidar: (id: string, aprovada: boolean, motivo?: string) => Promise<boolean>
  onPagar: (id: string, observacoes?: string) => Promise<boolean>
}

function formatMoney(value: number | null | undefined) {
  if (value == null) return '—'
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

export function FinanceNfDetailDrawer({
  open,
  loading,
  nf,
  onClose,
  onMarcarEmValidacao,
  onValidar,
  onPagar,
}: Props) {
  const [rejeicaoMotivo, setRejeicaoMotivo] = useState('')
  const [observacoes, setObservacoes] = useState('')
  const [acting, setActing] = useState(false)

  if (!open) return null

  const fileUrl = nf?.nfFileUrl ? resolvePublicAssetUrl(nf.nfFileUrl) : null

  const run = async (fn: () => Promise<boolean>) => {
    setActing(true)
    try {
      await fn()
    } finally {
      setActing(false)
    }
  }

  return (
    <div className='fixed inset-0 z-50 flex justify-end'>
      <button
        type='button'
        className='absolute inset-0 bg-black/50 backdrop-blur-sm'
        aria-label='Fechar'
        onClick={onClose}
      />
      <aside className='relative z-10 flex h-full w-full max-w-lg flex-col border-l border-[var(--glass-border)] bg-[var(--bg-card)] shadow-2xl'>
        <div className='flex items-center justify-between border-b border-[var(--glass-border)] px-5 py-4'>
          <div>
            <h2 className='text-lg font-semibold text-[var(--font-color-h2)]'>NF mensal</h2>
            <p className='text-sm text-[var(--font-color-muted)]'>
              {nf?.prestador?.nomeCompleto ?? '—'} · {nf?.competencia ?? ''}
            </p>
          </div>
          <button
            type='button'
            onClick={onClose}
            className='rounded-md p-1.5 text-[var(--text-muted)] hover:bg-[var(--glass-bg)]'
          >
            <X size={18} />
          </button>
        </div>

        <div className='flex-1 overflow-y-auto px-5 py-4'>
          {loading || !nf ? (
            <div className='flex justify-center py-16'>
              <div className='h-8 w-8 animate-spin rounded-full border-2 border-accent border-t-transparent' />
            </div>
          ) : (
            <div className='space-y-5'>
              <div>
                <span
                  className={cn(
                    'inline-flex px-2 py-0.5 rounded-full text-xs font-medium',
                    PJ_NF_STATUS_CLASS[nf.status],
                  )}
                >
                  {PJ_NF_STATUS_LABELS[nf.status]}
                </span>
              </div>

              {nf.enviadoComRessalvas && (
                <div className='rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-900'>
                  <p className='font-semibold'>NF enviada com ressalvas</p>
                  <p className='mt-1 text-[var(--font-color-body)]'>
                    {nf.alertasEnvio ?? 'Revise CNPJ e competência antes de validar o pagamento.'}
                  </p>
                </div>
              )}

              <dl className='grid gap-3 text-sm'>
                <div>
                  <dt className='text-xs uppercase text-[var(--font-color-label)]'>Valor esperado</dt>
                  <dd>{formatMoney(nf.valorEsperado ?? nf.prestador?.remuneracaoMensal)}</dd>
                </div>
                {nf.numeroNf && (
                  <div>
                    <dt className='text-xs uppercase text-[var(--font-color-label)]'>Número NF</dt>
                    <dd>{nf.numeroNf}</dd>
                  </div>
                )}
                {nf.dataEmissao && (
                  <div>
                    <dt className='text-xs uppercase text-[var(--font-color-label)]'>Data emissão</dt>
                    <dd>{new Date(nf.dataEmissao).toLocaleDateString('pt-BR')}</dd>
                  </div>
                )}
                {nf.enviadoEm && (
                  <div>
                    <dt className='text-xs uppercase text-[var(--font-color-label)]'>Enviado em</dt>
                    <dd>{new Date(nf.enviadoEm).toLocaleString('pt-BR')}</dd>
                  </div>
                )}
                {nf.rejeicaoMotivo && (
                  <div className='rounded-lg border border-red-500/20 bg-red-500/5 p-3'>
                    <dt className='text-xs uppercase text-red-400'>Motivo da rejeição</dt>
                    <dd className='mt-1 text-[var(--font-color-body)]'>{nf.rejeicaoMotivo}</dd>
                  </div>
                )}
              </dl>

              {fileUrl && (
                <div>
                  <p className='mb-2 text-xs uppercase text-[var(--font-color-label)]'>Anexo</p>
                  <a
                    href={fileUrl}
                    target='_blank'
                    rel='noreferrer'
                    className='inline-flex items-center gap-2 text-sm text-accent hover:underline'
                  >
                    {nf.nfFileName ?? (nf.nfMimeType?.includes('xml') ? 'Ver XML da NF' : 'Ver PDF da NF')}
                    <ExternalLink size={14} />
                  </a>
                </div>
              )}

              {nf.status === 'EM_VALIDACAO' && (
                <div className='space-y-3 border-t border-[var(--glass-border)] pt-4'>
                  <label className='block text-sm'>
                    <span className='mb-1 block text-[var(--font-color-muted)]'>
                      Motivo da rejeição (se aplicável)
                    </span>
                    <Input
                      value={rejeicaoMotivo}
                      onChange={(e) => setRejeicaoMotivo(e.target.value)}
                      placeholder='Descreva o motivo…'
                    />
                  </label>
                  <div className='flex flex-wrap gap-2'>
                    <Button
                      type='button'
                      variant='primary'
                      disabled={acting}
                      onClick={() => void run(() => onValidar(nf.id, true))}
                    >
                      Aprovar NF
                    </Button>
                    <Button
                      type='button'
                      variant='ghost'
                      disabled={acting || rejeicaoMotivo.trim().length < 3}
                      onClick={() =>
                        void run(() => onValidar(nf.id, false, rejeicaoMotivo.trim()))
                      }
                    >
                      Rejeitar
                    </Button>
                  </div>
                </div>
              )}

              {nf.status === 'APROVADA' && (
                <div className='space-y-3 border-t border-[var(--glass-border)] pt-4'>
                  <label className='block text-sm'>
                    <span className='mb-1 block text-[var(--font-color-muted)]'>Observações do pagamento</span>
                    <Input
                      value={observacoes}
                      onChange={(e) => setObservacoes(e.target.value)}
                      placeholder='Opcional'
                    />
                  </label>
                  <Button
                    type='button'
                    variant='primary'
                    disabled={acting}
                    onClick={() =>
                      void run(() => onPagar(nf.id, observacoes.trim() || undefined))
                    }
                  >
                    Confirmar pagamento
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        {nf && nf.status === 'NF_ENVIADA' && (
          <div className='border-t border-[var(--glass-border)] px-5 py-4'>
            <Button
              type='button'
              variant='primary'
              className='w-full'
              disabled={acting}
              onClick={() => void run(() => onMarcarEmValidacao(nf.id))}
            >
              Iniciar validação
            </Button>
          </div>
        )}
      </aside>
    </div>
  )
}
